var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { useClasses, useConsoleLog, useLayoutProps, useProps, useSkeletonLoader, } from '@hawkins/helpers';
import { IconCaretDown } from '@hawkins/icons/lib/iconCaretDown';
import { IconCaretUp } from '@hawkins/icons/lib/iconCaretUp';
import { Badge } from '../badge';
import { Dropdown } from '../dropdown';
import { useBadgeDropdownStyles } from './badgeDropdown.styles';
var displayName = 'BadgeDropdown';
/**
 * Implementation of the Badge Dropdown.
 * This will be wrapped by the version our users see, plain <BadgeDropdown />.
 * This will allow the users to see and set the generics, despite the shortcomings with
 * the React.forwardRef typings disallowing generic pass-through types.
 */
var BadgeDropdownImpl = React.forwardRef(function (props, ref) {
    var _a;
    // Props
    var _b = useProps(displayName, props), children = _b.children, classesProp = _b.classes, className = _b.className, color = _b.color, _c = _b.disabled, disabled = _c === void 0 ? false : _c, layout = _b.layout, loaderProps = _b.loaderProps, loading = _b.loading, onChange = _b.onChange, searchable = _b.searchable, styleProp = _b.style, value = _b.value, _d = _b.dropdownProps, dropdownProps = _d === void 0 ? {} : _d, restProps = __rest(_b, ["children", "classes", "className", "color", "disabled", "layout", "loaderProps", "loading", "onChange", "searchable", "style", "value", "dropdownProps"]);
    // Styles
    var baseClasses = useBadgeDropdownStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var badgeClassName = clsx(classes.badge, (_a = {}, _a[classes.badgeDisabled] = disabled, _a));
    // Content
    var badgeDropdownChildren = React.Children.toArray(props.children).filter(function (c) { return React.isValidElement(c) && c.type === Badge; });
    useConsoleLog(badgeDropdownChildren.length !== React.Children.count(props.children), 'children must be of type <Badge>, other children will be ignored', { displayName: displayName });
    var DropdownTrigger = React.useCallback(function (_a) {
        var option = _a.option, onClick = _a.onClick, focused = _a.focused, triggerRef = _a.triggerRef, onKeyDown = _a.onKeyDown;
        if (!option) {
            return null;
        }
        var Caret = focused ? IconCaretUp : IconCaretDown;
        return (_jsx(Badge, __assign({ ref: triggerRef, tabIndex: 0, onKeyDown: onKeyDown, className: badgeClassName, classes: { endAdornment: classes.badgeEndAdornment }, variant: option === null || option === void 0 ? void 0 : option.variant, onClick: onClick, color: option === null || option === void 0 ? void 0 : option.color, value: option === null || option === void 0 ? void 0 : option.value, disabled: disabled, endAdornment: _jsx(Caret, { className: classes.caret }) }, { children: option === null || option === void 0 ? void 0 : option.label })));
    }, [badgeClassName, classes.badgeEndAdornment, classes.caret, disabled]);
    var skeletonLoaderNode = useSkeletonLoader({
        loaderProps: __assign({ height: 24, variant: 'square', width: 111 }, loaderProps),
        loading: loading,
        style: style,
    });
    if (skeletonLoaderNode)
        return skeletonLoaderNode;
    var dropdownOptions = __spreadArray([], __read(badgeDropdownChildren.map(function (child) { return ({
        variant: child.props.variant,
        value: child.props.value,
        label: child.props.children,
        color: child.props.color,
        disabled: child.props.disabled,
    }); })), false);
    return (_jsx(Dropdown, __assign({ dropdownRef: ref, className: className, style: style, value: value ? __assign({}, value) : value, disabled: disabled, onChange: onChange, options: dropdownOptions, searchable: searchable, DropdownTrigger: DropdownTrigger }, restProps, dropdownProps)));
});
BadgeDropdownImpl.displayName = 'BadgeDropdownImpl';
/**
 * A specialized dropdown for `Badges`. Typically used to choose the status of something.
 *
 * BadgeDropdown requires there always be a value. BadgeDropdown always operates in a `controlled` manner.
 */
export function BadgeDropdown(props) {
    return _jsx(BadgeDropdownImpl, __assign({}, props));
}
BadgeDropdown.displayName = displayName;
